import { useState, type FormEvent } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocs } from '@/lib/useDocs';
import { normalizePhoneInput } from '@/lib/phone';
import { messageForAuthError } from '@/lib/authErrors';
import { formatDate, roleLabel } from '@/lib/format';
import type { Invite } from '@/types/domain';
import { strings } from '@/strings';

const inviteUser = callable<{ phone: string; role: string; name: string }, { phone: string }>('inviteUser');

/**
 * The referral model's front door: recommenders are invite-only, and this is
 * the only way anyone becomes one.
 *
 * Reached by admins and recruiters both, because the model depends on
 * recruiters bringing in recommenders — but the role list narrows for a
 * recruiter, since inviteUser refuses a staff role from one. Offering a choice
 * the server will reject is a worse interface than not offering it.
 *
 * `guest` is never offered: it is the ABSENCE of an invite, and buildInvite
 * refuses it outright.
 */
const INVITABLE = [
  { value: 'recommender', label: strings.roleRecommender, adminOnly: false },
  { value: 'candidate', label: strings.roleCandidate, adminOnly: false },
  { value: 'ngo-rep', label: strings.roleNgoRep, adminOnly: false },
  { value: 'recruiter', label: strings.roleRecruiter, adminOnly: true },
  { value: 'admin', label: strings.roleAdmin, adminOnly: true },
];

export function InvitesPage() {
  const { role: callerRole } = useAuth();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('recommender');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { rows, loading, error } = useDocs<Invite>(
    query(collection(db, 'invites'), orderBy('createdAt', 'desc')),
    []
  );
  const pending = rows.filter((row) => !row.usedAt);

  const canonical = normalizePhoneInput(phone);
  const choices = INVITABLE.filter((c) => !c.adminOnly || callerRole === 'admin');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canonical || busy) return;
    setBusy(true);
    setProblem(null);
    setDone(false);
    try {
      await inviteUser({ phone: canonical, role, name: name.trim() });
      setPhone('');
      setName('');
      setDone(true);
    } catch (err) {
      const code = (err as Error).message;
      // `already-registered` is thrown by two different callables and means two
      // different things: an organization already claimed by this account, or a
      // phone that already has an account at all. The shared map answers for the
      // organization case, so this page answers for its own.
      setProblem(
        code.includes('already-registered')
          ? strings.errAlreadyRegistered
          : messageForAuthError(code)
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <section className="flex flex-col gap-4">
        <h1 className="text-xl font-bold text-primary">{strings.inviteCreateTitle}</h1>
        <form onSubmit={submit} className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-white p-4">
          <label className="flex flex-col gap-1 text-sm" htmlFor="invite-phone">
            {strings.signInPhoneLabel}
            <input
              id="invite-phone"
              inputMode="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={strings.signInPhonePlaceholder}
              className="min-h-11 rounded-md border border-border-subtle px-3 py-2 text-right"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm" htmlFor="invite-name">
            {strings.inviteName}
            <input
              id="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="min-h-11 rounded-md border border-border-subtle px-3 py-2"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm" htmlFor="invite-role">
            {strings.inviteRole}
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="min-h-11 rounded-md border border-border-subtle px-3 py-2"
            >
              {choices.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            disabled={!canonical || busy}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {strings.inviteSubmit}
          </button>

          {done ? <p className="text-sm text-success">{strings.inviteCreated}</p> : null}
          {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
        </form>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-ink">{strings.invitePendingTitle}</h2>
        {loading ? <p className="text-muted">{strings.loading}</p> : null}
        {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
        {!loading && !error && pending.length === 0 ? (
          <p className="text-muted">{strings.invitesEmpty}</p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {pending.map((invite) => (
            <li
              key={invite.id}
              className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-white p-4 md:flex-row md:items-center md:justify-between md:gap-3"
            >
              <span className="font-medium text-ink">{invite.name || '—'}</span>
              <span className="text-sm text-muted" dir="ltr">{invite.id}</span>
              <span className="text-sm text-muted">{roleLabel(invite.role)}</span>
              <span className="text-sm text-muted" dir="ltr">{formatDate(invite.createdAt)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
